import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';

const CookieConsent = () => {
  const { i18n } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const consent = localStorage.getItem('xperience_cookie_consent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const acceptAll = () => {
    const consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('xperience_cookie_consent', JSON.stringify(consent));
    setShowBanner(false);
  };

  const acceptSelected = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('xperience_cookie_consent', JSON.stringify(consent));
    setShowBanner(false);
    setShowSettings(false);
  };

  const rejectAll = () => {
    const consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('xperience_cookie_consent', JSON.stringify(consent));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4"
      >
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {!showSettings ? (
            /* Main Banner */
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg mb-2">
                    {i18n.language === 'fr' 
                      ? 'Nous utilisons des cookies' 
                      : 'We use cookies'}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    {i18n.language === 'fr'
                      ? 'Nous utilisons des cookies pour améliorer votre expérience sur notre site. En continuant, vous acceptez notre utilisation des cookies conformément à notre politique de confidentialité.'
                      : 'We use cookies to improve your experience on our site. By continuing, you agree to our use of cookies in accordance with our privacy policy.'}
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={acceptAll} className="gap-2">
                      <Check className="w-4 h-4" />
                      {i18n.language === 'fr' ? 'Tout accepter' : 'Accept All'}
                    </Button>
                    <Button variant="outline" onClick={rejectAll}>
                      {i18n.language === 'fr' ? 'Refuser' : 'Reject'}
                    </Button>
                    <Button variant="ghost" onClick={() => setShowSettings(true)} className="gap-2">
                      <Settings className="w-4 h-4" />
                      {i18n.language === 'fr' ? 'Personnaliser' : 'Customize'}
                    </Button>
                  </div>
                </div>
                <button
                  onClick={rejectAll}
                  className="text-gray-400 hover:text-gray-600 p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : (
            /* Settings Panel */
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-lg">
                  {i18n.language === 'fr' ? 'Paramètres des cookies' : 'Cookie Settings'}
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium">
                      {i18n.language === 'fr' ? 'Cookies nécessaires' : 'Necessary Cookies'}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {i18n.language === 'fr'
                        ? 'Essentiels au fonctionnement du site'
                        : 'Essential for the website to function'}
                    </p>
                  </div>
                  <div className="w-12 h-6 bg-primary rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium">
                      {i18n.language === 'fr' ? 'Cookies analytiques' : 'Analytics Cookies'}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {i18n.language === 'fr'
                        ? 'Nous aident à comprendre l\'utilisation du site'
                        : 'Help us understand site usage'}
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.analytics ? 'bg-primary justify-end' : 'bg-gray-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <h4 className="font-medium">
                      {i18n.language === 'fr' ? 'Cookies marketing' : 'Marketing Cookies'}
                    </h4>
                    <p className="text-sm text-gray-500">
                      {i18n.language === 'fr'
                        ? 'Utilisés pour la publicité ciblée'
                        : 'Used for targeted advertising'}
                    </p>
                  </div>
                  <button
                    onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                    className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.marketing ? 'bg-primary justify-end' : 'bg-gray-300 justify-start'
                    }`}
                  >
                    <div className="w-4 h-4 bg-white rounded-full"></div>
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <Button onClick={acceptSelected} className="flex-1">
                  {i18n.language === 'fr' ? 'Enregistrer mes préférences' : 'Save Preferences'}
                </Button>
                <Button variant="outline" onClick={acceptAll}>
                  {i18n.language === 'fr' ? 'Tout accepter' : 'Accept All'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default CookieConsent;
